import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isLoggedIn } from "../../../utils/isLoggedIn";
import { buildSearchUrl } from "../../../utils/searchUtils";
import logo from "../../../images/bitcoin-live/logo.png";
import { NAV_LINKS, getRoleNavLinks } from "../constants";
import {
  applyDocumentTheme,
  getStoredTheme,
  persistThemePreference,
} from "../themeUtils";
import HeaderMemberMenu from "./HeaderMemberMenu";
import {
  HeaderBar,
  HeaderSpacer,
  HeaderInner,
  HeaderBrand,
  HeaderSearch,
  HeaderSearchIcon,
  HeaderSearchInput,
  BrandLink,
  BrandMark,
  BrandText,
  BrandAccent,
  Nav,
  NavLinkItem,
  HeaderActions,
  ThemeToggleButton,
  SignInButton,
  SubscribeButton,
  MenuToggle,
  MobileSearchButton,
  MobileSheetScrim,
  MobileSheetPanel,
  MobileSheetHeader,
  MobileSheetTitle,
  MobileSheetClose,
  MobileSheetNav,
  MobileSheetLink,
  MobileSheetFooter,
  MobileSheetPrimaryBtn,
  MobileSheetSecondaryBtn,
  MobileSheetLogoutBtn,
} from "./Header.styles";

const isNavLinkActive = (pathname, item) => {
  if (pathname === item.to) return true;
  if (item.to === "/blogs/public") {
    return pathname.startsWith("/blogs/public") || pathname.startsWith("/post/b");
  }
  if (item.to === "/blogs/premium") {
    return (
      pathname.startsWith("/blogs/premium") ||
      (pathname.startsWith("/post/") && !pathname.startsWith("/post/b"))
    );
  }
  if (item.to === "/cms") {
    return pathname.startsWith("/cms") || pathname.startsWith("/add-new-post");
  }
  if (item.to === "/admin") {
    return pathname.startsWith("/admin");
  }
  return false;
};

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const MoonIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
  </svg>
);

const SunIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const Header = ({ onSubscribeClick, hideSpacer = false, showSearch }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => getStoredTheme());
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const loggedIn = isLoggedIn();
  const user = getUserFromStorage();
  const navLinks = [...NAV_LINKS, ...getRoleNavLinks(user)];
  const isDark = theme === "dark";
  const searchEnabled = showSearch ?? pathname !== "/";

  useEffect(() => {
    applyDocumentTheme(theme);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const onResize = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) {
        setMenuOpen(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const handleToggleTheme = () => {
    const next = isDark ? "light" : "dark";
    setTheme(next);
    persistThemePreference(next);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = term.trim();
    navigate(query ? buildSearchUrl(query) : "/search");
  };

  const handleLogout = () => {
    closeMenu();
    localStorage.clear();
    navigate("/");
    window.setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const renderNavLink = (item) => {
    const isActive = isNavLinkActive(pathname, item);

    return (
      <NavLinkItem
        key={item.to}
        as={Link}
        to={item.to}
        $active={isActive}
        $showBorder
        aria-current={isActive ? "page" : undefined}
      >
        {item.label}
      </NavLinkItem>
    );
  };

  return (
    <>
      <HeaderBar>
        <HeaderInner>
          <HeaderBrand>
            <BrandLink as={Link} to="/" aria-label="Bitcoin Live home">
              <BrandMark src={logo} alt="" />
              <BrandText>
                bitcoin<BrandAccent>.live</BrandAccent>
              </BrandText>
            </BrandLink>
            {searchEnabled && (
              <HeaderSearch onSubmit={handleSearch}>
                <HeaderSearchIcon>
                  <SearchIcon />
                </HeaderSearchIcon>
                <HeaderSearchInput
                  type="text"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="Search…"
                  aria-label="Search site"
                  autoComplete="off"
                />
              </HeaderSearch>
            )}
          </HeaderBrand>

          <Nav aria-label="Primary">
            {navLinks.map((item) => renderNavLink(item))}
          </Nav>

          <HeaderActions>
            {searchEnabled ? (
              <MobileSearchButton
                as={Link}
                to="/search"
                aria-label="Search"
              >
                <SearchIcon />
              </MobileSearchButton>
            ) : null}

            <MenuToggle
              type="button"
              aria-label="Open site menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <span className="material-icons" aria-hidden="true">
                menu
              </span>
            </MenuToggle>

            <ThemeToggleButton
              type="button"
              onClick={handleToggleTheme}
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? <SunIcon /> : <MoonIcon />}
            </ThemeToggleButton>

            {loggedIn ? (
              <HeaderMemberMenu />
            ) : (
              <>
                <SignInButton as={Link} to="/login">
                  Sign In
                </SignInButton>
                <SubscribeButton type="button" onClick={onSubscribeClick}>
                  Subscribe
                </SubscribeButton>
              </>
            )}
          </HeaderActions>
        </HeaderInner>
      </HeaderBar>

      {menuOpen ? (
        <>
          <MobileSheetScrim role="presentation" onClick={closeMenu} />
          <MobileSheetPanel
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <MobileSheetClose
              type="button"
              aria-label="Close site menu"
              onClick={closeMenu}
            >
              <CloseIcon />
            </MobileSheetClose>

            <MobileSheetHeader>
              <MobileSheetTitle>
                bitcoin<BrandAccent>.live</BrandAccent>
              </MobileSheetTitle>
            </MobileSheetHeader>

            <MobileSheetNav aria-label="Site menu">
              {navLinks.map((item) => {
                const isActive = isNavLinkActive(pathname, item);
                return (
                  <MobileSheetLink
                    key={item.to}
                    as={Link}
                    to={item.to}
                    $active={isActive}
                    aria-current={isActive ? "page" : undefined}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </MobileSheetLink>
                );
              })}
            </MobileSheetNav>

            <MobileSheetFooter>
              {!loggedIn ? (
                <>
                  <MobileSheetPrimaryBtn
                    type="button"
                    onClick={() => {
                      closeMenu();
                      onSubscribeClick?.();
                    }}
                  >
                    Subscribe
                  </MobileSheetPrimaryBtn>
                  <MobileSheetSecondaryBtn
                    as={Link}
                    to="/login"
                    onClick={closeMenu}
                  >
                    Sign In
                  </MobileSheetSecondaryBtn>
                </>
              ) : (
                <>
                  <MobileSheetPrimaryBtn
                    as={Link}
                    to="/user-profile"
                    onClick={closeMenu}
                  >
                    <span className="material-icons" aria-hidden="true">
                      person
                    </span>
                    Profile
                  </MobileSheetPrimaryBtn>
                  <MobileSheetLogoutBtn type="button" onClick={handleLogout}>
                    <span className="material-icons" aria-hidden="true">
                      logout
                    </span>
                    Logout
                  </MobileSheetLogoutBtn>
                </>
              )}
            </MobileSheetFooter>
          </MobileSheetPanel>
        </>
      ) : null}

      {!hideSpacer && <HeaderSpacer />}
    </>
  );
};

export default Header;
